import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { CheckCircle2, Loader2, X } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { INTERESTS } from "../data/site";

const PreRegisterContext = createContext(null);
export const usePreRegister = () => useContext(PreRegisterContext);

export function PreRegisterProvider({ children }) {
  const [state, setState] = useState(null); // { interesse, origem }
  const [form, setForm] = useState({ nome: "", email: "", interesse: "", lgpd: false, news: false });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState("");
  const [done, setDone] = useState(false);
  const firstFieldRef = useRef(null);

  const openPreRegister = (opts = {}) => {
    setServerError("");
    setErrors({});
    setDone(false);
    setForm((f) => ({
      ...f,
      interesse: opts.interesse || "",
    }));
    setState({ interesse: opts.interesse || "", origem: opts.origem || "site" });
  };

  const close = () => setState(null);

  useEffect(() => {
    if (state) {
      document.body.style.overflow = "hidden";
      setTimeout(() => firstFieldRef.current?.focus(), 60);
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [state]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && close();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const validate = () => {
    const e = {};
    if (form.nome.trim().length < 3) e.nome = "Informe seu nome completo.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) e.email = "Informe um e-mail válido.";
    if (!form.interesse) e.interesse = "Selecione a modalidade de interesse.";
    if (!form.lgpd) e.lgpd = "É necessário confirmar a ciência sobre a Política de Privacidade.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    setServerError("");
    if (!validate()) return;
    setSubmitting(true);
    try {
      await api.post("/pre-registro", {
        nome: form.nome.trim(),
        email: form.email.trim(),
        interesse: form.interesse,
        origem: state?.origem || "site",
        consentimento_lgpd: true,
        consentimento_news: form.news,
      });
      setDone(true);
    } catch (err) {
      setServerError(
        formatApiError(
          err,
          "Não foi possível concluir seu cadastro neste momento. Tente novamente em alguns instantes."
        )
      );
    } finally {
      setSubmitting(false);
    }
  };

  const value = useMemo(() => ({ openPreRegister }), []);

  return (
    <PreRegisterContext.Provider value={value}>
      {children}
      <AnimatePresence>
        {state && (
          <motion.div
            key="pre-register-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[90] flex items-end justify-center bg-black/70 p-0 backdrop-blur-sm sm:items-center sm:p-6"
            onClick={close}
            data-testid="pre-register-modal-overlay"
          >
            <motion.div
              role="dialog"
              aria-modal="true"
              aria-label="Pré-cadastro OFF360"
              initial={{ opacity: 0, y: 40, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 24, scale: 0.98 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="relative w-full max-w-lg overflow-hidden rounded-t-3xl bg-white shadow-2xl sm:rounded-3xl"
              onClick={(e) => e.stopPropagation()}
              data-testid="pre-register-modal"
            >
              <div className="flex items-start justify-between bg-brand-obsidian px-6 py-5 sm:px-8">
                <div>
                  <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-brand-orange">
                    Pré-cadastro OFF360
                  </p>
                  <h2 className="mt-1 font-display text-xl font-bold text-white sm:text-2xl">
                    Em breve disponível para download!
                  </h2>
                  <p className="mt-1 text-sm text-slate-300">
                    O 360Taxi está chegando. Deixe seus dados para receber um aviso quando o aplicativo estiver disponível.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={close}
                  data-testid="modal-pre-cadastro-close"
                  aria-label="Fechar"
                  className="rounded-full p-2 text-slate-300 transition-colors hover:bg-white/10 hover:text-white"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {done ? (
                <div className="px-6 py-10 text-center sm:px-8" data-testid="pre-cadastro-success">
                  <CheckCircle2 className="mx-auto h-14 w-14 text-brand-green" />
                  <h3 className="mt-4 font-display text-2xl font-bold text-slate-900">
                    Pré-cadastro realizado!
                  </h3>
                  <p className="mx-auto mt-3 max-w-sm text-sm leading-relaxed text-slate-600">
                    Seu interesse foi registrado. Você poderá receber um aviso no e-mail informado quando o serviço
                    escolhido estiver disponível.
                  </p>
                  <button
                    type="button"
                    onClick={close}
                    data-testid="pre-cadastro-success-close"
                    className="mt-8 rounded-full bg-brand-orange px-8 py-3 text-sm font-bold text-white transition-colors hover:bg-brand-orangedark"
                  >
                    Fechar
                  </button>
                </div>
              ) : (
                <form className="space-y-4 px-6 py-6 sm:px-8" onSubmit={submit} noValidate>
                  <div>
                    <label htmlFor="pr-nome" className="text-sm font-semibold text-slate-700">
                      Nome completo *
                    </label>
                    <input
                      ref={firstFieldRef}
                      id="pr-nome"
                      data-testid="pre-cadastro-nome-input"
                      type="text"
                      autoComplete="name"
                      value={form.nome}
                      onChange={(e) => setForm({ ...form, nome: e.target.value })}
                      className="mt-1.5 w-full rounded-xl border border-slate-300 px-4 py-3 text-sm outline-none transition-colors focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/25"
                      placeholder="Seu nome completo"
                    />
                    {errors.nome && (
                      <p className="mt-1 text-xs font-semibold text-red-600" data-testid="pre-cadastro-nome-error">
                        {errors.nome}
                      </p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="pr-email" className="text-sm font-semibold text-slate-700">
                      E-mail *
                    </label>
                    <input
                      id="pr-email"
                      data-testid="pre-cadastro-email-input"
                      type="email"
                      autoComplete="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="mt-1.5 w-full rounded-xl border border-slate-300 px-4 py-3 text-sm outline-none transition-colors focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/25"
                      placeholder="voce@email.com"
                    />
                    {errors.email && (
                      <p className="mt-1 text-xs font-semibold text-red-600" data-testid="pre-cadastro-email-error">
                        {errors.email}
                      </p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="pr-interesse" className="text-sm font-semibold text-slate-700">
                      Tenho interesse em *
                    </label>
                    <select
                      id="pr-interesse"
                      data-testid="pre-cadastro-interesse-select"
                      value={form.interesse}
                      onChange={(e) => setForm({ ...form, interesse: e.target.value })}
                      className="mt-1.5 w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm outline-none transition-colors focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/25"
                    >
                      <option value="">Selecione uma modalidade</option>
                      {INTERESTS.map((i) => (
                        <option key={i} value={i}>
                          {i}
                        </option>
                      ))}
                    </select>
                    {errors.interesse && (
                      <p className="mt-1 text-xs font-semibold text-red-600" data-testid="pre-cadastro-interesse-error">
                        {errors.interesse}
                      </p>
                    )}
                  </div>

                  <div className="space-y-3 rounded-xl bg-slate-50 p-4">
                    <label className="flex cursor-pointer items-start gap-3">
                      <input
                        type="checkbox"
                        data-testid="pre-cadastro-consentimento-lgpd"
                        checked={form.lgpd}
                        onChange={(e) => setForm({ ...form, lgpd: e.target.checked })}
                        className="mt-0.5 h-5 w-5 shrink-0 accent-brand-orange"
                      />
                      <span className="text-xs leading-relaxed text-slate-600">
                        Li a{" "}
                        <Link
                          to="/politica-de-privacidade"
                          data-testid="pre-cadastro-link-privacidade"
                          className="font-semibold text-brand-orange hover:underline"
                        >
                          Política de Privacidade
                        </Link>{" "}
                        e estou ciente de como meus dados serão utilizados para o aviso solicitado. *
                      </span>
                    </label>
                    {errors.lgpd && (
                      <p className="text-xs font-semibold text-red-600" data-testid="pre-cadastro-lgpd-error">
                        {errors.lgpd}
                      </p>
                    )}
                    <label className="flex cursor-pointer items-start gap-3">
                      <input
                        type="checkbox"
                        data-testid="pre-cadastro-consentimento-news"
                        checked={form.news}
                        onChange={(e) => setForm({ ...form, news: e.target.checked })}
                        className="mt-0.5 h-5 w-5 shrink-0 accent-brand-orange"
                      />
                      <span className="text-xs leading-relaxed text-slate-600">
                        Quero receber também novidades, campanhas e ofertas do OFF360 por e-mail.
                      </span>
                    </label>
                  </div>

                  {serverError && (
                    <p className="rounded-lg bg-red-50 px-4 py-3 text-sm font-semibold text-red-700" data-testid="pre-cadastro-server-error">
                      {serverError}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="pre-cadastro-form-submit"
                    className="flex w-full items-center justify-center gap-2 rounded-full bg-brand-orange px-6 py-4 text-base font-bold text-white shadow-lg shadow-brand-orange/25 transition-all hover:bg-brand-orangedark disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {submitting && <Loader2 className="h-5 w-5 animate-spin" />}
                    Quero ser avisado
                  </button>
                </form>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </PreRegisterContext.Provider>
  );
}
